import React from 'react';
import { cn } from '@mellify/ui';

export interface MellifyLogoProps {
  /** Size of the logo icon */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the text label next to the icon */
  showText?: boolean;
  /** Additional className */
  className?: string;
  /** Text color class override */
  textClassName?: string;
}

const sizeMap = {
  sm: { container: 'w-8 h-8', text: 'text-base' },
  md: { container: 'w-10 h-10', text: 'text-lg' },
  lg: { container: 'w-12 h-12', text: 'text-xl' },
};

export const MellifyLogo: React.FC<MellifyLogoProps> = ({
  size = 'md',
  showText = true,
  className,
  textClassName,
}) => {
  const s = sizeMap[size];

  return (
    <span className={cn('inline-flex items-center gap-2.5 shrink-0 group', className)}>
      {/* Logo mark */}
      <span
        className={cn(
          s.container,
          'relative inline-flex items-center justify-center shrink-0 transition-transform duration-300 ease-smooth group-hover:scale-105'
        )}
      >
        <svg
          className="w-full h-full"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            {/* Primary gradient — rich purple depth */}
            <linearGradient id="ml-hex-bg" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#A78BFA" />
              <stop offset="40%" stopColor="#7C3AED" />
              <stop offset="100%" stopColor="#5B21B6" />
            </linearGradient>

            {/* Highlight gradient for glass layer */}
            <linearGradient id="ml-hex-highlight" x1="14" y1="4" x2="34" y2="28" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>

            {/* Inner shadow gradient */}
            <linearGradient id="ml-hex-inner" x1="24" y1="4" x2="24" y2="44" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#C4B5FD" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3B0764" stopOpacity="0.4" />
            </linearGradient>

            {/* Honey / amber accent gradient */}
            <linearGradient id="ml-honey" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="50%" stopColor="#FBBF24" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>

            {/* Bee body white with subtle warmth */}
            <linearGradient id="ml-bee-body" x1="20" y1="14" x2="26" y2="36" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F3F0FF" />
            </linearGradient>

            {/* Wing shimmer gradient */}
            <linearGradient id="ml-wing" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#DDD6FE" stopOpacity="0.5" />
            </linearGradient>

            {/* Subtle drop shadow */}
            <filter id="ml-shadow" x="-10%" y="-10%" width="120%" height="130%">
              <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#3B0764" floodOpacity="0.3" />
            </filter>

            {/* Outer glow for hover */}
            <filter id="ml-glow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feFlood floodColor="#7C3AED" floodOpacity="0.4" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            {/* Stripe pattern gradient */}
            <linearGradient id="ml-stripe" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#6D28D9" stopOpacity="0.75" />
              <stop offset="100%" stopColor="#7C3AED" stopOpacity="0.6" />
            </linearGradient>
          </defs>

          {/* ──────────── Hexagon base ──────────── */}
          {/* Outer hex with rounded corners feel */}
          <path
            d="M24 3L42.186 13.5V34.5L24 45L5.814 34.5V13.5L24 3Z"
            fill="url(#ml-hex-bg)"
          />

          {/* Inner depth layer */}
          <path
            d="M24 6L39.5 15V33L24 42L8.5 33V15L24 6Z"
            fill="url(#ml-hex-inner)"
            opacity="0.35"
          />

          {/* Glass highlight — top-left sheen */}
          <path
            d="M24 3L42.186 13.5V24L24 18L5.814 13.5L24 3Z"
            fill="url(#ml-hex-highlight)"
          />

          {/* Subtle border stroke for crispness */}
          <path
            d="M24 3L42.186 13.5V34.5L24 45L5.814 34.5V13.5L24 3Z"
            fill="none"
            stroke="#A78BFA"
            strokeWidth="0.5"
            strokeOpacity="0.3"
          />

          {/* ──────────── Bee icon ──────────── */}
          <g
            filter="url(#ml-shadow)"
            className="transition-transform duration-500 ease-smooth origin-center group-hover:translate-y-[-0.5px]"
          >
            {/* ── Antennae ── */}
            <path
              d="M20.5 16C19.5 13.5 17.8 12 17 12.3"
              stroke="#FFFFFF"
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.9"
            />
            <path
              d="M23 15.5C23.3 13 24.5 11.5 25.3 11.7"
              stroke="#FFFFFF"
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.9"
            />
            {/* Antenna tips — amber dots */}
            <circle cx="16.8" cy="12" r="1.1" fill="url(#ml-honey)" />
            <circle cx="25.5" cy="11.4" r="1.1" fill="url(#ml-honey)" />

            {/* ── Head ── */}
            <circle
              cx="21.5"
              cy="18.5"
              r="4"
              fill="url(#ml-bee-body)"
            />
            {/* Eye */}
            <circle cx="20.2" cy="17.8" r="1.3" fill="#5B21B6" />
            {/* Eye shine */}
            <circle cx="19.8" cy="17.4" r="0.45" fill="#FFFFFF" opacity="0.9" />

            {/* ── Body (torso) ── */}
            <ellipse
              cx="22.5"
              cy="27"
              rx="5.8"
              ry="7.8"
              fill="url(#ml-bee-body)"
              transform="rotate(-6 22.5 27)"
            />

            {/* ── Stripes ── */}
            <rect
              x="17"
              y="23"
              width="11"
              height="2.2"
              rx="1.1"
              fill="url(#ml-stripe)"
              transform="rotate(-6 22.5 24.1)"
            />
            <rect
              x="17.2"
              y="27"
              width="10.6"
              height="2.2"
              rx="1.1"
              fill="url(#ml-stripe)"
              transform="rotate(-6 22.5 28.1)"
            />
            <rect
              x="18"
              y="31"
              width="9"
              height="1.8"
              rx="0.9"
              fill="url(#ml-stripe)"
              opacity="0.7"
              transform="rotate(-6 22.5 31.9)"
            />

            {/* ── Wings ── */}
            {/* Right wing — larger, elegant */}
            <path
              d="M27 20.5C30.5 16.5 36.5 14 38 16.5C39.2 18.8 34.5 22.5 29 23.8L27 20.5Z"
              fill="url(#ml-wing)"
              className="transition-transform duration-700 ease-smooth origin-[27px_22px] group-hover:rotate-[-6deg]"
            />
            {/* Right wing inner line */}
            <path
              d="M28.5 20C31 17.5 34.5 16 35.5 17"
              stroke="#FFFFFF"
              strokeWidth="0.6"
              strokeOpacity="0.6"
              fill="none"
            />

            {/* Left wing — smaller, behind */}
            <path
              d="M18 19C16 15.5 12.5 13.5 11.5 15.3C10.5 17.1 14 20 18 21L18 19Z"
              fill="url(#ml-wing)"
              opacity="0.6"
              className="transition-transform duration-700 ease-smooth origin-[18px_20px] group-hover:rotate-[6deg]"
            />

            {/* ── Honey drop ── */}
            <path
              d="M32.5 26.5C32.5 26.5 35 30 35 32C35 33.8 33.6 35 32 35C30.4 35 29 33.8 29 32C29 30 31.5 26.5 31.5 26.5C31.8 26.1 32.2 26.1 32.5 26.5Z"
              fill="url(#ml-honey)"
            />
            {/* Honey drop shine */}
            <ellipse cx="31.3" cy="31" rx="1" ry="1.4" fill="#FFFFFF" opacity="0.35" />
          </g>
        </svg>

        {/* Hover glow ring */}
        <span className="absolute inset-0 rounded-xl bg-purple-500/0 group-hover:bg-purple-500/10 blur-lg transition-all duration-500" />
      </span>

      {showText && (
        <span className="inline-flex flex-col leading-none">
          <span
            className={cn(
              s.text,
              'font-bold font-display tracking-tight bg-clip-text',
              textClassName || 'text-dark-900 dark:text-dark-100'
            )}
          >
            Mellify
          </span>
        </span>
      )}
    </span>
  );
};

MellifyLogo.displayName = 'MellifyLogo';